// src/context/AuthContext.jsx
/* eslint-disable react-refresh/only-export-components -- standard React
   Context colocation (Provider + useAuth() hook + the decodeJwt helper
   they both need); splitting these across files would only hurt Fast
   Refresh granularity in dev, never correctness. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { authApi } from "../api/authApi";
import { api, getApiError } from "../api/client";

const AuthContext = createContext(null);

// Handles unicode payloads correctly (plain atob() mangles non-ASCII chars).
export function decodeJwt(token) {
  const payloadPart = token.split(".")[1];
  const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
      .join("")
  );
  return JSON.parse(json);
}

function userFromToken(token) {
  const payload = decodeJwt(token);
  return {
    id: payload.sub,
    email: payload.email,
    username: payload.username || payload.email,
    roles: payload.roles || [],
    // permissions is deliberately NOT read from the token — the backend no
    // longer bakes it in (see JwtClaims's own docstring: a role's grants
    // are resolved fresh per-request from `roles`, via a Redis-backed
    // cache, so an admin's permission-set edit is visible immediately
    // instead of only after re-login). The frontend's equivalent is
    // fetchPermissionsMap() below, via GET /auth/me.
    orgId: Number(payload.org_id || 0),
  };
}

export function normalizePerms(source) {
  if (!Array.isArray(source)) return [];
  return source
    .map((p) => (typeof p === "string" ? p : p?.name))
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

function readCachedUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

// A valid token can exist with no cached "user" blob (e.g. localStorage was
// partially cleared, or a future code path stores the token without also
// storing the user) — re-derive the same fields synchronously from the
// token itself, in useState's lazy initializer (not a useEffect), so
// isAuthenticated is correct on the very first render with no loading
// flash and no risk of ProtectedOutlet redirecting to /login before this
// resolves. ProtectedOutlet separately revalidates against a live read
// once mounted.
function resolveInitialUser() {
  const cached = readCachedUser();
  if (cached) return cached;
  const token = localStorage.getItem("access_token");
  if (!token) return null;
  try {
    const derived = userFromToken(token);
    localStorage.setItem("user", JSON.stringify(derived));
    return derived;
  } catch {
    localStorage.removeItem("access_token");
    return null;
  }
}

const EMPTY_PERMISSIONS_MAP = { global_permissions: [], accounts: {}, projects: {} };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(resolveInitialUser);
  // Engagement-scoped permissions, merged on top of the org-wide ones (see
  // WorkspaceContext.selectEngagement) — kept here, not in WorkspaceContext,
  // so hasPermission() is the one place every consumer checks regardless of
  // whether a permission came from the base map or the current selection.
  const [scopedPermissions, setScopedPermissions] = useState([]);
  // The full nested {global_permissions, accounts, projects} map, fetched
  // ONCE from GET /auth/me (see
  // effective_permissions_cache.py on the backend — this is its whole
  // point: every account/project the user can reach, resolved up front,
  // so switching between them is a local lookup via getScopedPermissions()
  // below instead of a network round trip per switch). Starts "loading"
  // only if a user is already known at boot (a cached/token-derived user
  // with nothing fetched yet), so an anonymous visit to /login doesn't sit
  // in a permanent loading state.
  const [permissionsMap, setPermissionsMap] = useState(EMPTY_PERMISSIONS_MAP);
  const [permissionsLoading, setPermissionsLoading] = useState(() => !!user);

  const fetchPermissionsMap = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const res = await authApi.effectivePermissions();
      setPermissionsMap(res.data || EMPTY_PERMISSIONS_MAP);
    } catch (e) {
      // Falling back to empty permissions is still correct (fail closed,
      // never grant by default) -- but doing it silently made a transient
      // fetch failure indistinguishable from a genuine "you don't have
      // this permission", e.g. every create/manage button across the app
      // looking permanently disabled with no indication anything broke.
      setPermissionsMap(EMPTY_PERMISSIONS_MAP);
      toast.error(
        `Couldn't load your permissions (${getApiError(e)}) — some actions may look unavailable. Try refreshing.`
      );
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const login = useCallback(
    (token) => {
      localStorage.setItem("access_token", token);
      const nextUser = userFromToken(token);
      localStorage.setItem("user", JSON.stringify(nextUser));
      if (nextUser.email) localStorage.setItem("last_login_email", nextUser.email);
      setUser(nextUser);
      fetchPermissionsMap();
      return nextUser;
    },
    [fetchPermissionsMap]
  );

  const logout = useCallback(async () => {
    try {
      // ── TWO-WAY SSO: CENTRALIZED LOGOUT ──
      // Deletes the shared google_id_token/sso_session cookies backend-side
      // so this logout ends the session across every app in the ecosystem,
      // not just this tab's localStorage.
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout error", e);
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("current_account_id");
    localStorage.removeItem("current_engagement_id");
    // Deliberately no setUser(null)/setPermissionsMap() here: the hard
    // navigation below tears down this whole React tree anyway, so
    // updating local state first only causes a visible flicker --
    // ProtectedOutlet reacts to isAuthenticated flipping false by
    // client-side-navigating to /login via <Navigate>, which paints for an
    // instant right before this line's full page reload replaces it with
    // the same destination a second time.
    window.location.href = "/login";
  }, []);

  // Page refresh: resolveInitialUser() already reconstructed `user`
  // synchronously (from cached localStorage or the token) so isAuthenticated
  // is correct on the very first render, but permissions are no longer part
  // of either source — fetch them once here. login()/logout() handle every
  // later transition, so this only ever needs to run on mount.
  useEffect(() => {
    if (user) {
      fetchPermissionsMap();
    } else {
      setPermissionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local lookup into the already-loaded map — WorkspaceContext calls this
  // on selectAccount()/selectEngagement() instead of re-fetching from the
  // server, since the whole map was already loaded once at login.
  const getScopedPermissions = useCallback(
    ({ accountId, projectId } = {}) => {
      const fromAccount =
        accountId != null ? permissionsMap.accounts?.[String(accountId)] : null;
      const fromProject =
        projectId != null ? permissionsMap.projects?.[String(projectId)] : null;
      return [...(fromAccount || []), ...(fromProject || [])];
    },
    [permissionsMap]
  );

  const basePerms = useMemo(
    () => normalizePerms(permissionsMap.global_permissions),
    [permissionsMap]
  );
  const roleSet = useMemo(() => new Set(normalizePerms(user?.roles || [])), [user]);

  // context, when given, checks global permissions plus THAT SPECIFIC
  // account/project's own bucket only — never the currently-selected
  // project's scopedPermissions, even if that happens to be a different
  // account/project than the one context names. (Checking allPerms here
  // used to leak: allPerms includes scopedPermissions, so once ANY
  // currently-selected project granted a permission, hasPermission(name,
  // {accountId: X}) returned true for every X, not just the ones that
  // actually granted it.) Omitted (the common case), this checks exactly
  // what it always has: global + currently-selected.
  const hasPermission = useCallback(
    (name, context) => {
      const target = String(name).toLowerCase();
      if (basePerms.includes(target)) return true;
      if (context) {
        return normalizePerms(getScopedPermissions(context)).includes(target);
      }
      return normalizePerms(scopedPermissions).includes(target);
    },
    [basePerms, scopedPermissions, getScopedPermissions]
  );
  // Ignores scopedPermissions entirely, even without a context — for
  // checks that must reflect a true org-wide grant regardless of whatever
  // project happens to be currently selected (e.g. "can I create a
  // project with no account at all").
  const hasGlobalPermission = useCallback(
    (name) => basePerms.includes(String(name).toLowerCase()),
    [basePerms]
  );
  const hasAnyPermission = useCallback(
    (names = [], context) => names.some((n) => hasPermission(n, context)),
    [hasPermission]
  );
  // True if any of `names` is granted anywhere at all -- global, or on ANY
  // account/project the user can reach -- regardless of what's currently
  // selected in the workspace switcher. hasPermission()/hasAnyPermission()
  // deliberately don't answer this (they only check global + the
  // currently-selected scope); this is for a link/page whose relevance
  // isn't tied to whichever account happens to be active right now.
  const hasPermissionAnywhere = useCallback(
    (names = []) => {
      const targets = names.map((n) => String(n).toLowerCase());
      if (targets.some((t) => basePerms.includes(t))) return true;
      const buckets = [
        ...Object.values(permissionsMap.accounts || {}),
        ...Object.values(permissionsMap.projects || {}),
      ];
      return buckets.some((bucket) => {
        const normalized = normalizePerms(bucket);
        return targets.some((t) => normalized.includes(t));
      });
    },
    [basePerms, permissionsMap]
  );
  const hasRole = useCallback(
    (name) => roleSet.has(String(name).toLowerCase()),
    [roleSet]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      permissionsLoading,
      permissionsMap,
      login,
      logout,
      hasPermission,
      hasGlobalPermission,
      hasAnyPermission,
      hasPermissionAnywhere,
      hasRole,
      setScopedPermissions,
      getScopedPermissions,
    }),
    [
      user,
      permissionsLoading,
      permissionsMap,
      login,
      logout,
      hasPermission,
      hasGlobalPermission,
      hasAnyPermission,
      hasPermissionAnywhere,
      hasRole,
      getScopedPermissions,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
